import { StanButton, StanModal, StanTag } from '@/components';
import { ProductPayment } from '../types/transactionTypes';
import IcDownloadAttachment from '@/assets/icons/ic-file-download.svg';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { calculatePaymentAmounts, isDisputeWon } from '@/features/income/income.logic';
import { createIconElement, GENERIC_STATUS, logWithSentry, PAYMENT_STATUS, stanAnalytics, stanNotify } from '@/lib';
import { useAuthStore } from '@/stores';
import { useModal } from '@/hooks/useModal';
import { RefundModal } from './RefundModal';
import { useUser } from '@/queries/useUser';
import { FulfillOrderModal } from './FulfillOrderModal';
import { UndeliveredTransactionModal } from './UndeliveredTransactionModal';
import IcMoney from '@/assets/icons/ic-currency-dollar-circle.svg';
import { useTransactionCalculations } from '../hooks/useTransactionCalculations';
import { useConfirmUnfulfillOrder, useResendBooking, useResendDigitalDownload } from '@/queries/useIncome';
import { useSendCourseLink } from '@/queries/useCourses';
import { PRODUCT_TYPES } from '@/lib/constants';

export function PaymentButtonArea({
    paymentData,
    onRefreshData,
}: {
    paymentData: ProductPayment;
    onRefreshData: () => void;
}) {
    const { paymentId } = useParams();
    const { user } = useUser();
    const isAdminLoggedInAsUser = useAuthStore(state => state.isAdminLoggedInAsUser);
    const { paymentGatewayDisplayName } = useTransactionCalculations(paymentData);
    const { posNetRevenue, posGrossAmount, posFeeAmount } = calculatePaymentAmounts(paymentData);
    const { mutate: sendCourseLink, isPending: isSendingCourseLink } = useSendCourseLink();
    const { mutate: resendDigitalDownload, isPending: isResendingDigitalDownload } = useResendDigitalDownload();
    const { mutate: resendBooking, isPending: isResendingBooking } = useResendBooking();
    const { mutate: confirmUnfulfillOrder, isPending: isConfirmingUnfulfillOrder } = useConfirmUnfulfillOrder();
    const { isOpen: isRefundModalOpen, open: openRefundModal, close: closeRefundModal } = useModal();
    const { isOpen: isOrderFulfillModalOpen, open: openOrderFulfillModal, close: closeOrderFulfillModal } = useModal();
    const { isOpen: isUndeliveredModalOpen, open: openUndeliveredModal, close: closeUndeliveredModal } = useModal();
    const { isOpen: isUnfulfilledModalOpen, open: openUnfulfilledModal, close: closeUnfulfilledModal } = useModal();

    const hasFileAttached = paymentData.data.file_name && paymentData.data.file_url ? true : false;
    const isEmailUndelivered =
        paymentData.status === PAYMENT_STATUS.CREATOR_FULFILLED && paymentData.data.email_delivered === false
            ? true
            : false;
    const isDisputed = paymentData.status === PAYMENT_STATUS.DISPUTED;
    const isCustomerActive = paymentData.user_fan_status !== GENERIC_STATUS.DELETED;
    const isFulfillmentProduct = paymentData.product_type === 'fulfillment';
    const hasFileAttachedSection = isFulfillmentProduct
        ? paymentData.status === PAYMENT_STATUS.PAID_NOT_FULFILLED || hasFileAttached
            ? true
            : false
        : false;
    const disputeWon = isDisputeWon(paymentData);
    const paymentGateway = paymentData.payment_gateway;
    const isRefundable = useMemo(() => {
        if (paymentData.product_type === 'payout') {
            return false;
        }
        const refundableTil = paymentData.data?.refundable_til;
        const isRefundableLocal = [PAYMENT_STATUS.CREATOR_FULFILLED, PAYMENT_STATUS.PAID_NOT_FULFILLED].includes(
            paymentData.status
        );
        // TODO: VERIFY LOGIC -> old logic disputeIsInquiry is undefined ALWAYS
        const paymentStatusRefundable = isRefundableLocal && !isDisputed;

        return (
            !!paymentData.revenue &&
            paymentData.revenue > 0 &&
            paymentStatusRefundable &&
            (!refundableTil || new Date(refundableTil) > new Date()) &&
            (paymentData?.product_type !== 'affiliate-fee' || paymentData.data.payment_held_by_platform === 0)
        );
    }, [paymentData, isDisputed]);

    const handleResendDigitalDownload = () => {
        if (paymentId)
            resendDigitalDownload(paymentId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Digital Download Sent',
                        text: 'Digital Download has been sent!',
                    });
                },
                onError: (error: any) => {
                    stanNotify({
                        type: 'error',
                        title: 'Digital Download could not be sent, let us help you with that!',
                        text: error?.response?.data?.message || 'Please email friends@stanwith.me',
                        duration: 5000,
                    });
                },
            });
    };

    const handleSendCourseLink = () => {
        if (paymentId)
            sendCourseLink(paymentId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Link Sent',
                        text: 'Course Link has been sent!',
                        duration: 2000,
                    });
                },
                onError: (error: any) => {
                    stanNotify({
                        type: 'error',
                        title: 'Course link could not be sent, let us help you with that!',
                        text: error?.response?.data?.message || 'Please email friends@stanwith.me',
                        duration: 5000,
                    });
                },
            });
    };

    const handleResendBooking = () => {
        if (paymentId)
            resendBooking(paymentId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Booking Sent',
                        text: 'Booking has been sent!',
                    });
                },
                onError: (error: any) => {
                    stanNotify({
                        type: 'error',
                        title: 'Booking could not be sent, let us help you with that!',
                        text: error?.response?.data?.message || 'Please email friends@stanwith.me',
                        duration: 5000,
                    });
                },
            });
    };

    const handleConfirmUnfulfillOrder = (paymentId: string | number) => {
        if (paymentId)
            confirmUnfulfillOrder(paymentId, {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Success!',
                        text: 'Transaction Unfulfilled',
                        duration: 5000,
                    });
                    onRefreshData();
                },
                onError: (error: any) => {
                    stanNotify({
                        type: 'error',
                        title: 'Something went wrong',
                        text: 'Please contact us at friends@stanwith.me',
                        duration: 5000,
                    });
                    logWithSentry('failed-to-unfulfill-order', {
                        context: { error },
                    });
                },
            });
    };

    const RefundIcon = createIconElement(IcMoney);
    return (
        <>
            {paymentData?.status === PAYMENT_STATUS.REFUNDED && (
                <StanTag label='Refunded' className='bg-neutral-subtle text-info-dark' />
            )}
            {paymentData?.status === PAYMENT_STATUS.FAILED && (
                <>
                    <StanTag
                        label={paymentData?.data.retrying ? 'Retrying' : 'Failed'}
                        className='bg-negative-soft text-negative-bold'
                    />
                    {paymentData?.data.retrying && paymentData?.data.failure_reason && (
                        <span className='text-gray text-sm'>• Reason: {paymentData.data.failure_reason}</span>
                    )}
                </>
            )}
            {isEmailUndelivered && (
                <StanButton
                    name='payment-details-update-delivery-button'
                    variant='outline'
                    onClick={openUndeliveredModal}
                    size='xs'
                    color='destructive'
                    className='w-full md:w-auto'
                >
                    Update Delivery
                </StanButton>
            )}
            {isRefundable && (
                <StanButton
                    name='payment-details-refund-button'
                    size='xs'
                    className='w-full md:w-auto'
                    variant='outline'
                    icon={RefundIcon}
                    onClick={() => {
                        stanAnalytics('refund-modal-opened', {
                            meta: { user_id: user?.user_id, username: user?.username },
                            props: {
                                payment_id: paymentId,
                                paymentGateway: paymentGateway,
                            },
                        });
                        openRefundModal();
                    }}
                >
                    Refund
                </StanButton>
            )}
            {isCustomerActive && paymentData.status === PAYMENT_STATUS.CREATOR_FULFILLED && isFulfillmentProduct && (
                <StanButton
                    name='payment-details-unfulfill-order-button'
                    variant='outline'
                    onClick={openUnfulfilledModal}
                    size='xs'
                    className='w-full md:w-auto'
                >
                    Unfulfill Order
                </StanButton>
            )}
            {isCustomerActive &&
                paymentData.status === PAYMENT_STATUS.CREATOR_FULFILLED &&
                paymentData.product_type === PRODUCT_TYPES.COURSE && (
                    <StanButton
                        name='payment-details-send-course-link-button'
                        variant='outline'
                        onClick={handleSendCourseLink}
                        loading={isSendingCourseLink}
                        size='xs'
                        className='w-full md:w-auto'
                    >
                        Resend Course Link
                    </StanButton>
                )}
            {isCustomerActive &&
                paymentData.status === PAYMENT_STATUS.CREATOR_FULFILLED &&
                paymentData.product_type === PRODUCT_TYPES.DIGITAL_DOWNLOAD && (
                    <StanButton
                        name='payment-details-resend-digital-download-button'
                        onClick={handleResendDigitalDownload}
                        loading={isResendingDigitalDownload}
                        size='xs'
                        variant='outline'
                        className='w-full md:w-auto'
                    >
                        Resend Product
                    </StanButton>
                )}
            {isCustomerActive &&
                paymentData.status === PAYMENT_STATUS.CREATOR_FULFILLED &&
                paymentData.product_type === PRODUCT_TYPES.MEETING &&
                isAdminLoggedInAsUser() && (
                    <StanButton
                        name='payment-details-resend-booking-button'
                        onClick={handleResendBooking}
                        loading={isResendingBooking}
                        size='xs'
                        variant='outline'
                        className='w-full md:w-auto'
                    >
                        Resend Booking
                    </StanButton>
                )}
            {hasFileAttachedSection && (
                <div className='py-3'>
                    {isCustomerActive && paymentData.status === PAYMENT_STATUS.PAID_NOT_FULFILLED && (
                        <StanButton
                            name='payment-details-resend-booking-button'
                            onClick={openOrderFulfillModal}
                            size='xs'
                            className='w-full md:w-auto'
                        >
                            Fulfill Order
                        </StanButton>
                    )}
                    {hasFileAttached && paymentData.status !== PAYMENT_STATUS.PAID_NOT_FULFILLED && (
                        <div className='ml-2'>
                            <h6 className='text-light mb-1 pb-1 text-sm!'>Attachment</h6>
                            <a href={paymentData?.data.file_url} className='mb-0 inline-flex' download target='_blank'>
                                <p className='text-dark col-auto truncate px-0 text-sm'>
                                    {paymentData?.data.file_name}
                                </p>
                                <IcDownloadAttachment className='size-auto px-0 pl-1' />
                            </a>
                        </div>
                    )}
                </div>
            )}
            {isDisputed &&
                (disputeWon ? (
                    <StanTag label='Dispute Won' className='bg-seafoam-subtle text-seafoam-dark !hidden md:!flex' />
                ) : (
                    <StanTag label='Disputed' className='bg-negative-soft text-negative-bold !hidden md:!flex' />
                ))}

            <RefundModal
                isOpen={isRefundModalOpen}
                onClose={closeRefundModal}
                orderRefundData={{
                    netRevenue: posNetRevenue || '',
                    grossAmount: posGrossAmount,
                    processingFeeAmount: posFeeAmount || '',
                    refundFeeLabel: `${paymentGatewayDisplayName}'s Refund Fee`,
                }}
                orderAmount={paymentData?.pos_data.gross_amount?.value ?? 0}
                paymentGateway={paymentGateway}
                user={user}
                onRefund={onRefreshData}
                orderId={paymentId}
            />
            <FulfillOrderModal
                isOpen={isOrderFulfillModalOpen}
                onClose={closeOrderFulfillModal}
                transactionData={paymentData}
                orderId={paymentId}
                onOrderFulfilled={onRefreshData}
            />
            <UndeliveredTransactionModal
                isOpen={isUndeliveredModalOpen}
                onClose={closeUndeliveredModal}
                onOrderResent={onRefreshData}
                orderDetails={paymentData}
                orderId={paymentId}
            />
            <StanModal
                id='unfulfill-order-modal'
                isOpen={isUnfulfilledModalOpen}
                onClose={closeUnfulfilledModal}
                primaryActionLabel='Update'
                title='Just Confirming!'
                primaryActionLoading={isConfirmingUnfulfillOrder}
                onPrimaryAction={() => {
                    handleConfirmUnfulfillOrder(paymentId ?? -1);
                    closeUnfulfilledModal();
                }}
            >
                <span className='text-light mb-4 block text-center text-sm'>
                    Order status will be updated to Unfulfilled
                </span>
            </StanModal>
        </>
    );
}
